import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Filters,
  InlineStack,
  Text,
  Pagination,
  SkeletonBodyText,
  EmptyState,
  Box,
  Popover,
  ActionList,
  Button,
} from '@shopify/polaris';
import { MenuHorizontalIcon } from '@shopify/polaris-icons';
import { clinicPortalApi } from '@/services';
import type { Patient } from '@/types';

function getStatusBadge(status: string) {
  const tone = status === 'active' ? 'success' : status === 'inactive' ? 'critical' : undefined;
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <Badge tone={tone}>{label}</Badge>;
}

function PatientActionMenu({ patient }: { patient: Patient }) {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const [active, setActive] = useState(false);

  return (
    <Popover
      active={active}
      activator={
        <Button
          icon={MenuHorizontalIcon}
          variant="plain"
          onClick={() => setActive((a) => !a)}
          accessibilityLabel="Actions"
        />
      }
      onClose={() => setActive(false)}
    >
      <ActionList
        items={[
          {
            content: 'Create Order',
            onAction: () => {
              setActive(false);
              navigate(`${base}/orders/new?patientId=${patient.id}`);
            },
          },
          {
            content: 'View Dashboard',
            onAction: () => {
              setActive(false);
              navigate(`${base}/patients/${patient.id}`);
            },
          },
        ]}
        onActionAnyItem={() => setActive(false)}
      />
    </Popover>
  );
}

export default function ClinicPatients() {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['clinic-patients', page, searchQuery],
    queryFn: () =>
      clinicPortalApi.getPatients({
        page,
        limit,
        search: searchQuery || undefined,
      }),
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const rows = (data?.data || []).map((patient: Patient) => [
    `${patient.firstName} ${patient.lastName}`,
    patient.email || 'N/A',
    patient.phone || 'N/A',
    patient.referredByDoctor
      ? `Dr. ${patient.referredByDoctor.firstName} ${patient.referredByDoctor.lastName}`
      : 'N/A',
    getStatusBadge(patient.status),
    <PatientActionMenu key={patient.id} patient={patient} />,
  ]);

  if (error) {
    return (
      <Page title="Patients">
        <Card>
          <EmptyState
            heading="Error loading patients"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Patients"
      primaryAction={{
        content: 'Add Patient',
        onAction: () => navigate(`${base}/patients/new`),
      }}
    >
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search by name or email..."
            filters={[]}
            appliedFilters={[]}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={() => setSearchQuery('')}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No patients found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Patients associated with your clinic will appear here.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Name', 'Email', 'Phone', 'Doctor', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} patients
          </Text>
        </Box>
      )}
    </Page>
  );
}
